import React, { useState, useEffect, useMemo } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { IconButton, TextField, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import RefreshButton from 'components/elements/refresh-button/RefreshButton';
import { getGeneratedPPTsAPI, getSignedUrl } from 'ServerAPI/serverAPI';
import actionIcon from "../../assets/images/course/action-icon.svg";
import { useToast } from 'context/toast-context/ToastProvider';
import { DateDisplay } from 'helper/utils';
import { useNavigate } from 'react-router-dom';
import { Download } from "lucide-react";
import { useCookies } from 'react-cookie';


// PPTGenerationTable Component
export const PPTGenerationTable  = ({ userId, setPPTCount }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [cookies] = useCookies();

    // Fetch data function
    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setIsRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            if (!userId) {
                showToast('User ID not found in cookies', "error");
                return;
            }

            const response = await getGeneratedPPTsAPI(userId);

           if (response.data && response.data.ppts) {
                setData(response.data.ppts);
                setPPTCount((response.data.ppts).length);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error('Error fetching generated PPT:', err);
            setError(err.message || 'Failed to fetch generated PPT');
            setData([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [userId]);

    // Handle refresh
    const handleRefresh = () => {
        fetchData(true);
    };

    // Handle search
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle generate new PPT
    const handleGenerateNewPPT = () => {
        navigate('/ppt-generation/');
    };

    //Handle download PPT
    const handleDownloadPPT = async (pptId) => {
        try {
            showToast('Download started!', 'warning');
            const payload = {
                ppt: true,
                userId: cookies?.userId, 
                pptId: pptId,
                fileName: pptId,
                method: "GET",
            };

            const response = await getSignedUrl(payload);

            if (response.data?.preSignedUrl || response.data?.objectUrl) {
                const downloadUrl = response.data.preSignedUrl || response.data.objectUrl;

                // Trigger download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = 'presentation.pptx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToast('PPT downloaded successfully!', 'success');
            } else {
                showToast('Failed to get PPT URL', 'error');
            }
        } catch (error) {
            console.error('Error downloading PPT:', error);
            showToast('Failed to download PPT', 'error');
        }
    };


    // Handle view PPT
    // const handleViewPPT = (pptId) => {
    //     navigate(`/ppt-generation/${pptId}`);
    // };

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        return data.filter(item =>
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.status?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    // Table columns configuration
    const columns = useMemo(() => [
        {
            id: 'sno',
            header: 'S.No.',
            cell: ({ row }) => row.index + 1,
            enableSorting: false,
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ getValue }) => (
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                    {getValue() || 'N/A'}
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ getValue }) => {
                const status = getValue() || 'unknown';
                const statusColors = {
                    completed: 'success',
                    processing: 'warning',
                    failed: 'danger',
                    inProgress: 'secondary'
                };
                return (
                    <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Created At',
            cell: ({ getValue }) => (
                <span className="text-muted small">
                    <DateDisplay timeString={getValue()} />
                </span>
            ),
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Tooltip title="Download PPT">
                    <IconButton>
                        <span
                            onClick={() => {
                                handleDownloadPPT(row.original.pptId);
                            }}
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Download color="rgb(34, 77, 153)" size={24} />
                        </span>
                    </IconButton>
                </Tooltip>
            ),
            enableSorting: false,
        },
    ], []);

    // Render loading state
    if (loading && !isRefreshing) {
        return (
            <div className="text-center p-5">
                <CircularProgress />
                <div className="mt-2">Loading PPT...</div>
            </div>
        );
    }

    return (
        <>
            {/* Search and Action Bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search for title, status"
                    InputProps={{ startAdornment: <SearchIcon /> }}
                    onChange={handleSearch}
                    value={searchTerm}
                    className="w-100"
                    style={{ maxWidth: '400px' }}
                />
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <RefreshButton
                        handleRefresh={handleRefresh}
                        title="Refresh PPT"
                        isRefreshing={isRefreshing}
                    />
                    <Button
                        variant="primary"
                        onClick={handleGenerateNewPPT}
                        className="d-flex align-items-center gap-1"
                    >
                        <AddIcon fontSize="small" />
                        Generate New PPT
                    </Button>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error</Alert.Heading>
                    <p className="mb-0">{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => fetchData()}
                        >
                            Try Again
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Content Area */}
            <div className="mt-3">
                {!error && (
                    <>
                        {filteredData.length === 0 ? (
                            <div className="text-center p-5">
                                <div className="mb-3">
                                    <i className="fas fa-video fa-3x text-muted"></i>
                                </div>
                                <h5 className="text-muted">
                                    {searchTerm ? 'No PPT found matching your search' : 'No generated PPT found'}
                                </h5>
                                <p className="text-muted">
                                    {searchTerm
                                        ? 'Try adjusting your search terms or clear the search to see all PPT.'
                                        : 'Get started by generating your first PPT.'
                                    }
                                </p>
                                {!searchTerm && (
                                    <Button
                                        variant="primary"
                                        onClick={handleGenerateNewPPT}
                                        className="mt-2"
                                    >
                                        <AddIcon fontSize="small" className="me-1" />
                                        Generate New PPT
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <TanstackTable
                                data={filteredData}
                                columns={columns}
                                pagination={true}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};